import { translate } from '@vimeo/i18n-runtime';

import {
  PLANS,
  PLANS_ORDER,
  PLANS_SUBHEADING,
  UNSUPPORTED_PLANS,
  PLANS_DEFAULT_FEATURES,
  SEAT_TIER_PLANS,
  LEGACY_PLANS,
  PLANS_DEFAULT_VIDEO_QUOTAS,
  SOLUTION_PLANS,
  DEFAULT_SEAT_PLANS,
  NEW_PLANS,
  CREATOR_V2_SKUS,
  CREATOR_V2_FEATURES,
} from './constants';
import { defaultTemplateProps, UserData, PlanData, TrackingProps } from './interface';
import translations from './translations';

// Return modal header text
// If header text is not passed in as props uses default string
//
export const getModalHeader = (headerText: string | undefined) => {
  if (headerText) {
    return headerText;
  } else {
    return translations.defaultHeader;
  }
};

export const getModalSubheader = (subHeaderText: string | undefined) => {
  if (subHeaderText === undefined) {
    return translations.defaultSubHeader;
  } else {
    return subHeaderText;
  }
};

// Function to check if the user is elgible for free trial. Currently if the user
// is having any paid plan do not allow free trial.
//
export const isEligibleForFreeTrial = (userData: UserData) => {
  return (
    (userData.membership?.type === PLANS.FREE || userData.membership?.type === PLANS.BASIC) &&
    !userData.membership?.subscription?.trial?.hasBeenInFreeTrial
  );
};

// Checks if we should show monthly plans to the user based on their plansData
// Currently, not all legacy tiers support monthly, so verify user is on seat tiers
//
export const showMonthlyPlans = (plansData: PlanData[]) => {
  return (
    plansData.find((planData) => planData.metadata?.purchasedProduct?.isMonthly) !== undefined &&
    isSeatTierPlan(plansData[0].tier)
  );
};

export const getPlanBillingInfoCopy = (displayMonthlyPlan: boolean) => {
  if (displayMonthlyPlan) return translations.monthlyBillingInfo;
  return translations.yearlyBillingInfo;
};

export const getPlanBillingFrequencyCopy = (
  displayMonthlyPlan: boolean,
  planData?: PlanData,
  isBandwidthProduct?: boolean,
) => {
  if (displayMonthlyPlan) return translations.billedMonthly;
  if (
    planData &&
    ![PLANS.PRO, PLANS.BUSINESS, PLANS.PREMIUM].includes(planData.tier) &&
    !isBandwidthProduct
  )
    return translations.orPriceBilledMonthly(planData);
  return translations.billedAnnually;
};

export const getPlanCTALink = (
  planData: PlanData,
  displayMonthlyPlan: boolean,
  campaignOverride?: string | null,
) => {
  const { annual, monthly } = planData.metadata.interactions.purchase.uri;
  const baseUrl = displayMonthlyPlan ? monthly : annual;
  if (campaignOverride) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}campaignId=${campaignOverride}&usePaymentsService=true`;
  }
  return baseUrl;
};

// Format plan price to include currency symbol.
//
export const getPlanDisplayPrice = (
  planData: PlanData,
  displayMonthlyPlan: boolean,
  isBandwidthProduct?: boolean,
) => {
  if (isBandwidthProduct) {
    return `${planData.priceFormatted.annual}`;
  }
  return `${
    displayMonthlyPlan ? planData.priceFormatted.monthly : planData.priceFormatted.annualMonthly
  }`;
};

// UK VAT is hardcoded at 20% on the frontend.
export const UK_VAT_RATE = 0.2;

export const isUkLocation = (location?: string) => location?.toUpperCase() === 'GB';

const formatCurrency = (amount: number, currencyCode: string, locale = 'en-GB') => {
  const rounded = Math.round(amount * 100) / 100;
  const isWhole = rounded % 1 === 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  } catch {
    return `${isWhole ? rounded.toFixed(0) : rounded.toFixed(2)} ${currencyCode}`;
  }
};

// For UK users, returns the price string with 20% VAT included; otherwise null.
export const getUkVatDisplayPrice = (
  planData: PlanData,
  displayMonthlyPlan: boolean,
  isBandwidthProduct?: boolean,
) => {
  const currencyCode = planData.currency?.currencyCode;
  if (!currencyCode) return null;

  let baseAmount: number | undefined;
  if (isBandwidthProduct) {
    baseAmount = planData.price.annual;
  } else if (displayMonthlyPlan) {
    baseAmount = planData.price.monthly;
  } else {
    baseAmount = planData.price.annualMonthly;
  }

  if (typeof baseAmount !== 'number') return null;

  return formatCurrency(baseAmount * (1 + UK_VAT_RATE), currencyCode);
};

// Function to filter out the plans which needs to be shown that is received
// from the plans api.
//
export const filterPlansData = (
  plansData: PlanData[],
  userData: UserData,
  excludePlans: Array<PLANS> = [],
) => {
  return plansData.filter((planData: PlanData) => {
    return showPlan(planData, userData, excludePlans);
  });
};

/* Allow plans to be shown only if the plan meets the following conditions.
  1. Plan should not be in the unsupported list.
  2. Plan should not be current user plan.
  3. Plan should be of the higher tire than the current user plan.
*/
const showPlan = (planData: PlanData, userData: UserData, excludePlans: Array<PLANS> = []) => {
  return (
    !excludePlans.includes(planData.tier) &&
    !UNSUPPORTED_PLANS.includes(planData.tier) &&
    userData.membership?.type !== planData.tier &&
    ((PLANS_ORDER as any)[planData.tier] ?? -1) >
      ((PLANS_ORDER as any)[userData.membership?.type] ?? -1)
  );
};

export const getPlanSubHeading = (
  modalConfig: defaultTemplateProps | undefined,
  planData: PlanData,
) => {
  const subHeading = modalConfig?.planOverrides?.[planData.tier]?.subHeading;

  return subHeading === undefined ? (PLANS_SUBHEADING as any)[planData.tier] : subHeading;
};

export const getBandwidthSubHeader = (planData: PlanData) => {
  const isBandwidthProduct = planData.metadata.isBandwidthProduct;

  if (!isBandwidthProduct) {
    return null;
  }
  if (planData.tier === 'enterprise') {
    return translate('More Bandwidth');
  }
  return translate({
    singular: '{BANDWIDTH} annual bandwidth',
    replacements: {
      BANDWIDTH: planData?.metadata?.entitlements?.params?.bandwidth?.periodicQuota,
    },
  });
};

export const getPlanFeaturesList = (
  modalConfig: defaultTemplateProps | undefined,
  planData: PlanData,
) => {
  const featuresList = modalConfig?.planOverrides?.[planData.tier]?.featuresList;
  const legacyPlanStorageCopy = getLegacyPlanStorageCopy(planData);

  return featuresList === undefined
    ? isCreatorV2Plan(planData)
      ? CREATOR_V2_FEATURES
      : legacyPlanStorageCopy
        ? [legacyPlanStorageCopy, ...(PLANS_DEFAULT_FEATURES as any)[planData.tier]]
        : (PLANS_DEFAULT_FEATURES as any)[planData.tier]
    : featuresList;
};

export const showRecommendedBadge = (
  planData: PlanData,
  index: number,
  previousPlanTier: PLANS | null,
) => {
  const planIndex =
    previousPlanTier && [PLANS.STARTER, PLANS.PLUS].includes(previousPlanTier) ? index - 1 : index;

  return (
    planIndex === 0 &&
    (SEAT_TIER_PLANS.includes(planData.tier) || LEGACY_PLANS.includes(planData.tier)) &&
    planData.tier !== PLANS.STARTER &&
    planData.tier !== PLANS.PLUS
  );
};

export const getVuid = () => {
  const VUID_REGEX = /vuid=(\w+\.\w+);?/;
  const match = document.cookie.match(VUID_REGEX);

  return match && match.length >= 2 ? match[1] : null;
};

export const getLanguage = () => {
  const LANAGUAGE_REGEX = /language=(\w+);?/;
  const match = document.cookie.match(LANAGUAGE_REGEX);

  return match && match.length >= 2 ? match[1] : null;
};

export const isSeatTierPlan = (plan: PLANS) => {
  return SEAT_TIER_PLANS.includes(plan);
};

export const isRepackagingTier = (plan: PLANS) => {
  return NEW_PLANS.includes(plan);
};

export const isCreatorV2Plan = (planData?: PlanData) => {
  return (
    planData?.tier === PLANS.CREATOR &&
    (CREATOR_V2_SKUS.includes(planData.id?.monthly ?? '') ||
      CREATOR_V2_SKUS.includes(planData.id?.annual ?? ''))
  );
};

// copied from @vimeo/ui/core/utils/getDeviceType
//
export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  )
    return 'mobile';
  return 'desktop';
};

export const hasDisplayPlan = (obj: any): obj is { displayPlan: PLANS } =>
  obj && obj.hasOwnProperty('displayPlan');

export const getRecommendedPlanData = (
  plansData: PlanData[],
  userData: UserData,
  displayPlan: PLANS | undefined,
) => {
  if (displayPlan) {
    return plansData.find((plan) => plan.tier === displayPlan);
  }

  return filterPlansData(plansData, userData)[0];
};

export const getPreviousPlanTierCopy = (
  filteredPlans: PlanData[],
  planData: PlanData,
  index: number,
) => {
  const isSeatTier = SEAT_TIER_PLANS.includes(planData.tier);
  const isSolutionTier = SOLUTION_PLANS.includes(planData.tier);

  if (index > 0) {
    return filteredPlans[index - 1].tier;
  }

  let plansArray: PLANS[] = [];
  if (isSeatTier) {
    plansArray = DEFAULT_SEAT_PLANS;
  } else if (isSolutionTier) {
    plansArray = SOLUTION_PLANS;
  } else {
    plansArray = LEGACY_PLANS;
  }
  const planIndex = plansArray.findIndex((tier) => planData.tier === tier);
  return plansArray[planIndex - 1];
};

// Return Enterprise modal Title text
// If Title text is not passed in as props, use the default string
export const getEnterpriseModalTitle = (titleText: string | undefined) =>
  titleText || translations.doMoreWithEnterprise;

// Return Enterprise modal Subtitle text
// If Subtitle text is not passed in as props, use the default string
export const getEnterpriseSubtitle = (subTitleText: string | undefined) =>
  subTitleText === undefined ? translations.productBenefits : subTitleText;

export const getEnterpriseFooterLinkText = (text: string | undefined) =>
  text === undefined ? translations.morePlanDetails : text;

// Format plan price to include currency symbol.
export const getPlanDisplayPriceForTooltip = (planData: PlanData, displayMonthlyPlan: boolean) => {
  return `${planData.currency.symbol}${
    displayMonthlyPlan ? planData.price.monthly : planData.price.annual
  }`;
};

export const getPlanToolTipCopy = (
  planData: PlanData,
  displayMonthlyPlan: boolean,
  LINE_BREAK_TAG: () => any,
) =>
  displayMonthlyPlan
    ? translate({
        singular: 'Purchase additional seats {LINE_BREAK} for {PLAN_SEAT_PRICE} per seat per month',
        replacements: {
          PLAN_SEAT_PRICE: getPlanDisplayPriceForTooltip(planData, displayMonthlyPlan),
          LINE_BREAK: LINE_BREAK_TAG,
        },
      })
    : translate({
        singular: 'Purchase additional seats {LINE_BREAK} for {PLAN_SEAT_PRICE} per seat per year',
        replacements: {
          PLAN_SEAT_PRICE: getPlanDisplayPriceForTooltip(planData, displayMonthlyPlan),
          LINE_BREAK: LINE_BREAK_TAG,
        },
      });

const getLegacyPlanStorageCopy = (planData: PlanData) => {
  const { tier, metadata } = planData;
  const quotaPeriod = metadata.entitlements?.params?.videoStorageQuotaPeriod;
  const freeQuota =
    metadata.entitlements?.params?.videoStoragePeriodicQuota ||
    metadata.entitlements?.params?.videoStorageQuotaCap;
  const periodicQuota =
    tier === 'free' && freeQuota !== null
      ? freeQuota
      : metadata.entitlements?.params?.videoStoragePeriodicQuota || 0;

  // Lifetime plans
  if (quotaPeriod === 'lifetime' && !isSeatTierPlan(tier)) {
    return translate({
      singular: '{AMOUNT} total storage',
      replacements: {
        AMOUNT: periodicQuota,
      },
    });
  }

  // Periodic cap plans
  if (tier === 'pro' || tier === 'plus') {
    return translate({
      singular: '{AMOUNT} / week',
      replacements: {
        AMOUNT: periodicQuota,
      },
    });
  } else {
    return null;
  }
};

export const getPlanQuota = (planData: PlanData) =>
  (PLANS_DEFAULT_VIDEO_QUOTAS as any)[planData.tier] || null;

export const isSolutionTier = (tier: PlanData['tier']) => SOLUTION_PLANS.includes(tier);

export const appendTrackingQueryParams = (url: string, trackingData: TrackingProps['params']) => {
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}upsell=${
    trackingData?.upsell_name || 'none'
  }&integration=${trackingData?.integration || 'none'}&feature=${trackingData?.feature || 'none'}`;
};

export const appendPaywallParams = (
  url: string,
  paywallTracking: TrackingProps['paywallTracking'],
): string => {
  if (!url || !paywallTracking.paywallTrigger) return url;
  const params = new URLSearchParams({
    paywall_trigger: paywallTracking.paywallTrigger,
    paywall_location: paywallTracking.paywallLocation,
    paywall_type: paywallTracking.paywallType,
    paywall_feature: paywallTracking.paywallFeature,
  });
  return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
};

export const appendExtraQueryParams = (
  url: string,
  extraQueryParams?: Record<string, string>,
): string => {
  if (!url || !extraQueryParams || Object.keys(extraQueryParams).length === 0) return url;

  const params = new URLSearchParams(extraQueryParams);
  return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
};

/**
 * Compares user's plan tier with a comparison plan
 * @param userPlan - User's current plan (e.g., from viewer.user.account)
 * @param comparisonPlan - Plan to compare against
 * @returns 1 if userPlan is higher, 0 if equal, -1 if lower, null if comparison is not possible
 *
 * @example
 * comparePlanTier('advanced', 'standard') // 1 (advanced is higher than standard)
 * comparePlanTier('starter', 'advanced') // -1 (starter is lower than advanced)
 * comparePlanTier('standard', 'standard') // 0 (equal)
 */
export const comparePlanTier = (userPlan?: string, comparisonPlan?: PLANS): number | null => {
  if (!userPlan || !comparisonPlan) return null;

  // Normalize userPlan to PLANS enum if possible
  const normalizedUserPlan = userPlan as PLANS;

  const userPlanOrder = (PLANS_ORDER as any)[normalizedUserPlan];
  const comparisonPlanOrder = (PLANS_ORDER as any)[comparisonPlan];

  if (userPlanOrder === undefined || comparisonPlanOrder === undefined) {
    return null;
  }

  if (userPlanOrder > comparisonPlanOrder) return 1;
  if (userPlanOrder < comparisonPlanOrder) return -1;
  return 0;
};

/**
 * Checks if user's plan tier is higher or equal to comparison plan
 * @param userPlan - User's current plan (e.g., from viewer.user.account)
 * @param comparisonPlan - Plan to compare against
 * @returns true if userPlan is higher or equal to comparisonPlan
 */
export const isUserPlanHigherOrEqualThan = (userPlan?: string, comparisonPlan?: PLANS): boolean => {
  const result = comparePlanTier(userPlan, comparisonPlan);
  return result === 1 || result === 0;
};
